import AssessmentVIFCharts, {
  getLabels
} from '@components/Charts/AssessmentVIFCharts'
import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import { useQuery } from '@tanstack/react-query'
import { AssessmentReportVIFLevelOptions } from '@utils/Variables'
import {
  GetAPIparams,
  renderTitle,
  useDropdownMenu
} from '@utils/assessmentReportUtils'
import { objToLevSelOpts } from '@utils/enumHelper'
import { appSessionStorage } from '@utils/storageUtils'
import { Flex } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { useLocation } from '@suntex.core/nav'
import { Database_Procs_TeamVIF_Item, TeamId } from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { $EntityLevel, EntityLevel } from '@suntex.fim/enums'
import { useFramework, useRotateDevice } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { LevelSelect, LoaderOverlay, SearchInput } from '@suntex.fim/ui'

import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import {
  VIFReportData,
  allColumnKey,
  columns,
  getVIFColumnKeys,
  hidableSelectLabel,
  schoolColumns,
  schoolExportColumn
} from './AssessmentReport/AssessmentReportVIFTable'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

export const veryImportantFactLevelOptions = objToLevSelOpts(
  AssessmentReportVIFLevelOptions
)

function AssessmentReportVeryImportantFact() {
  const { api } = useFramework()
  const location = useLocation()
  const {
    vifLevel: queryVifLevel,
    grade: queryGrade,
    year: queryYear,
    hiddenDatasets
  } = useAppQueryString()
  useRotateDevice()
  const { login } = useAuth(isAuthenticated)
  const isSchool =
    login?.entityLevel === EntityLevel.School &&
    location.pathname.includes('school')
  const team = useOptionalTeamInfo()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [dropdownFilters, setDropDownFilters] = useAssessmentDropdownFilters()

  const { id, year, grade } = GetAPIparams(isSchool, dropdownFilters)
  const selectedLevel = {
    level: Number(queryVifLevel) || appSessionStorage.vifLevel
  }
  const [filters, setFilters] = useState(selectedLevel)
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  const [isExporting, setIsExporting] = useState(false)

  const { data: summaryRecordData } = useQuery({
    queryKey: ['assessment', 'vifAggregatedSummary', id, isSchool, year, grade],
    queryFn: async () => {
      const response =
        await api.get_Assessment_VeryImportantFactsAggregatedByLevel({
          id: id!,
          level: isSchool ? login.entityLevel : EntityLevel.Team,
          year: year!,
          aggregateLevel: login?.entityLevel,
          grade
        })
      return response.vifAggregatedByLevels?.[0] ?? null
    },
    enabled: id != null
  })

  const { data: assessmentReport = [], isLoading } = useQuery({
    queryKey: ['assessment', 'vifTeam', id, isSchool, year, grade],
    queryFn: async () => {
      if (isSchool && login.entityLevel) {
        const response =
          await api.get_Assessment_VeryImportantFactsAggregatedByLevel({
            id: id!,
            level: login.entityLevel,
            year: year!,
            aggregateLevel: $EntityLevel.from(login.entityLevel - 1),
            grade
          })
        return response.vifAggregatedByLevels || []
      } else {
        const response = await api.get_Assessment_VeryImportantFactsTeam({
          teamId: id as TeamId
        })
        return response.teamVeryImportantFacts || []
      }
    },
    enabled: id != null
  })
  /**
   * open modal for send message and set sendMessageData state value
   */
  const sendMessage = (data: any[]) => {
    setIsModalOpen(true)

    setSendMessageData(data)
  }
  const memoColumns = useMemo<ColumnType<VIFReportData>[]>(
    () =>
      isSchool
        ? schoolColumns({ graphColor: '#3FB2FF', level: filters.level })
        : columns({ graphColor: '#3FB2FF', level: filters.level, sendMessage }),
    [filters.level]
  )
  const columnKeys = getVIFColumnKeys(filters.level)
  const linkOptions = columnKeys.map((label) => ({
    key: `word-problems/${filters.level}/${label}`,
    label: hidableSelectLabel[label]
  }))
  /**
   * Initialize usetable with initial data and
   * Destructure table and Table from useTable hooks
   */
  const { table, Table } = useAssessmentTable<VIFReportData>({
    data: assessmentReport,
    columns: memoColumns,
    isLoading,
    globalFilter: searchQuery,
    initialSorting: [{ id: isSchool ? 'name' : 'displayName', desc: false }]
  })

  const selectedRow = table.getSelectedRowModel().flatRows
  // Update localStorage whenever level changes
  useEffect(() => {
    appSessionStorage.vifLevel = filters.level
  }, [filters.level])
  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: Database_Procs_TeamVIF_Item }) => ({
      label: original.displayName || original.userId,
      value: original.playerId
    })
  )

  const fileName = isSchool ? `${login.schoolName}_VIF` : `${team?.name}_VIF`
  const exportColumn = isSchool
    ? schoolExportColumn(filters.level)
    : hidableSelectLabel
  const items = useDropdownMenu({
    table,
    hidableSelectLabel: exportColumn,
    fileName,
    hasColumnChange: isSchool
      ? ['grade', ...getLabels(filters.level)]
      : allColumnKey,
    transformCols: isSchool
      ? (id: string, colValue: any) => {
          if (!colValue) {
            return {}
          }
          return Object.entries(colValue).reduce((newVale, [key, value]) => {
            return {
              ...newVale,
              [`${hidableSelectLabel[id][0]}${key}`]: value || 0
            }
          }, {})
        }
      : (id: string, colValue: any[]) => {
          return colValue.reduce((newVale, [key, value]) => {
            return {
              ...newVale,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              [`${hidableSelectLabel[id]} ${key}`]: value || 0
            }
          }, {})
        },
    setIsLoading: setIsExporting
  })
  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay isLoading={isLoading || isExporting} />
        <AssessmentReportNavigation
          filters={
            <Flex gap={14}>
              <Flex flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">Mode</p>
                <LevelSelect
                  value={filters.level}
                  onChange={(level) => {
                    setFilters((prev) => ({ ...prev, level }))
                    setSearchQuery('')
                  }}
                  options={veryImportantFactLevelOptions}
                />
              </Flex>
            </Flex>
          }
          dropdownFilters={dropdownFilters}
          setDropDownFilters={setDropDownFilters}
          isSchool={isSchool}
        />

        <Flex className="tiles-container" wrap="wrap">
          <Flex className="left" vertical gap="20px" flex="1 1 560px">
            <AssessmentReportSummaryDashboard
              dropdownFilters={dropdownFilters}
              isSchool={isSchool}
            />
          </Flex>
          <Flex className="right" flex="1 1 560px">
            <Flex vertical className="graph-tiles" justify="space-between">
              {renderTitle(isSchool)}
              <Flex vertical align="center" className="graph-tiles__graph">
                <div className="graph-container">
                  {/* <img src={AggregateGraphPurple} alt="goal index chart" /> */}
                  <AssessmentVIFCharts
                    students={summaryRecordData?.students || 1}
                    chartData={
                      summaryRecordData || {
                        vif_add_completed_count: 0,
                        vif_add_rules_dd_completed_count: 0,
                        vif_add_1_completed_count: 0,
                        vif_sub_completed_count: 0,
                        vif_sub_rules_dd_completed_count: 0,
                        vif_sub_2_completed_count: 0,
                        vif_mul_rules_dd_2_completed_count: 0,
                        vif_mul_completed_count: 0,
                        vif_mul_1_completed_count: 0,
                        vif_div_completed_count: 0,
                        vif_div_rules_dd_completed_count: 0,
                        vif_div_1_completed_count: 0
                      }
                    }
                    level={filters.level}
                  />
                </div>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {/* result */}
        <div className="main-result">
          {/* Updated toolbar design */}
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">
              {isSchool ? 'School Teams' : 'Team Players'}
            </h4>

            <Flex
              gap="10px"
              justify="space-between"
              wrap
              className="print-hidden"
            >
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              {login?.entityLevel !== EntityLevel.School && (
                <Button
                  intent="tertiary"
                  size="small"
                  disabled={isDisabled}
                  onClick={() => setIsModalOpen(true)}
                >
                  SEND MESSAGE
                </Button>
              )}
            </Flex>
          </Flex>
          <Table />
        </div>
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
          />
        )}
        <SendMessage
          linkOptions={linkOptions}
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)

            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
          reportType="Very Important Facts"
        />
      </Flex>
    </>
  )
}

export default AssessmentReportVeryImportantFact
